import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';

// Helper function to extract date string from date object or string
function getDateString(dateValue) {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') return dateValue;
  if (typeof dateValue === 'object' && dateValue.iso) return dateValue.iso;
  return '';
}

export default function MaintenanceHistoryChart({ maintenanceHistory = [], showTitle = true }) {
  // Process the maintenance history data to create chart data
  const processChartData = () => {
    if (!maintenanceHistory || maintenanceHistory.length === 0) {
      return [];
    }

    // Find the earliest and latest dates
    const dates = maintenanceHistory
      .map(visit => {
        const dateStr = getDateString(visit.date);
        return dateStr ? new Date(dateStr) : null;
      })
      .filter(date => date !== null && !isNaN(date.getTime()));
    
    if (dates.length === 0) {
      return [];
    }
    
    const earliestDate = new Date(Math.min(...dates.map(d => d.getTime())));
    const latestDate = new Date(Math.max(...dates.map(d => d.getTime())));
    
    // Get current date
    const currentDate = new Date();
    const endDate = currentDate > latestDate ? currentDate : latestDate;
    
    // Generate all months from earliest to current using UTC to avoid timezone issues
    // Add +1 to month to fix timezone offset
    const allMonths = [];
    const startMonth = new Date(Date.UTC(earliestDate.getUTCFullYear(), earliestDate.getUTCMonth() + 1, 1));
    const endMonth = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth() + 1, 1));
    
    console.log('[MaintenanceHistoryChart] Date range:', {
      earliestDate: earliestDate.toISOString(),
      latestDate: latestDate.toISOString(),
      startMonth: startMonth.toISOString(),
      endMonth: endMonth.toISOString()
    });
    
    // Iterate through months using UTC to avoid timezone issues
    let currentMonth = new Date(startMonth);
    while (currentMonth <= endMonth) {
      const monthKey = `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, '0')}`;
      console.log('[MaintenanceHistoryChart] Generated month:', monthKey);
      allMonths.push({
        month: monthKey,
        visits: 0
      });
      // Move to next month in UTC
      currentMonth = new Date(Date.UTC(
        currentMonth.getUTCFullYear(),
        currentMonth.getUTCMonth() + 1,
        1
      ));
    }
    
    console.log('[MaintenanceHistoryChart] All generated months:', allMonths.map(m => m.month));
    
    // Count visits per month
    console.log('[MaintenanceHistoryChart] Processing visits:', maintenanceHistory.length);
    maintenanceHistory.forEach((visit, index) => {
      console.log(`[MaintenanceHistoryChart] Visit ${index}:`, {
        rawDate: visit.date,
        dateType: typeof visit.date,
        isObject: typeof visit.date === 'object',
        hasIso: visit.date?.iso
      });
      
      const dateStr = getDateString(visit.date);
      console.log(`[MaintenanceHistoryChart] Visit ${index} - extracted dateStr:`, dateStr);
      
      if (!dateStr) {
        console.warn(`[MaintenanceHistoryChart] Visit ${index} - no dateStr extracted`);
        return;
      }
      
      const date = new Date(dateStr);
      console.log(`[MaintenanceHistoryChart] Visit ${index} - Date object:`, {
        date: date.toString(),
        iso: date.toISOString(),
        utcYear: date.getUTCFullYear(),
        utcMonth: date.getUTCMonth(),
        localYear: date.getFullYear(),
        localMonth: date.getMonth()
      });
      
      if (isNaN(date.getTime())) {
        console.warn(`[MaintenanceHistoryChart] Visit ${index} - invalid date`);
        return;
      }
      
      // Use UTC methods to avoid timezone issues, add +1 to month to fix offset
      const monthKey = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 2).padStart(2, '0')}`;
      console.log(`[MaintenanceHistoryChart] Visit ${index} - monthKey:`, monthKey);
      
      const monthData = allMonths.find(m => m.month === monthKey);
      if (monthData) {
        monthData.visits += 1;
        console.log(`[MaintenanceHistoryChart] Visit ${index} - added to month ${monthKey}, total visits:`, monthData.visits);
      } else {
        console.warn(`[MaintenanceHistoryChart] Visit ${index} - month ${monthKey} not found in allMonths`);
        console.log('[MaintenanceHistoryChart] Available months:', allMonths.map(m => m.month));
      }
    });
    
    console.log('[MaintenanceHistoryChart] Final chart data:', allMonths);
    return allMonths;
  };

  const chartData = processChartData();
  console.log('[MaintenanceHistoryChart] Chart data after processing:', chartData);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '0.75rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <p style={{ margin: '0 0 0.25rem 0', fontWeight: '600', color: '#374151' }}>
            {new Date(label + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
          </p>
          <p style={{ margin: '0', color: '#6b7280', fontSize: '0.875rem' }}>
            Visits: <span style={{ fontWeight: '600', color: '#374151' }}>{data.visits}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Format x-axis labels
  const formatXAxisLabel = (tickItem) => {
    const date = new Date(tickItem + '-01');
    return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  // Format period labels for summary
  const formatPeriodLabel = (tickItem) => {
    const date = new Date(tickItem + '-01');
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (chartData.length === 0) {
    return (
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: showTitle ? '1.5rem' : '1rem',
        marginBottom: showTitle ? '1.5rem' : '0',
        flex: showTitle ? 'none' : '1',
        minHeight: showTitle ? 'auto' : '200px'
      }}>
        {showTitle && (
          <h3 style={{ 
            margin: '0 0 1rem 0', 
            fontSize: '1.125rem', 
            fontWeight: '600', 
            color: '#374151' 
          }}>
            Maintenance Visits per Month
          </h3>
        )}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: showTitle ? '200px' : '150px',
          color: '#9ca3af',
          fontSize: '0.875rem',
          fontStyle: 'italic'
        }}>
          No maintenance history available
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: '#fff',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: showTitle ? '0.875rem' : '1rem',
      marginBottom: showTitle ? '1.5rem' : '0',
      flex: showTitle ? '0.8' : '1',
      minHeight: showTitle ? 'auto' : '200px',
      height: '240px'
    }}>
      {showTitle && (
        <h3 style={{ 
          margin: '0 0 0.5rem 0', 
          fontSize: '0.9rem', 
          fontWeight: '600', 
          color: '#374151' 
        }}>
          Maintenance Visits per Month
        </h3>
      )}
      
      <div style={{ height: showTitle ? '160px' : '200px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 15, bottom: 5 }}>
            <defs>
              <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#2C5AA2" stopOpacity={0.3}/>
                <stop offset="95%" stopColor="#2C5AA2" stopOpacity={0.05}/>
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="month" 
              tickFormatter={formatXAxisLabel}
              stroke="#6b7280"
              fontSize={11}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={11}
              label={showTitle ? { 
                value: 'Visits per Month', 
                angle: -90, 
                position: 'insideLeft',
                style: { textAnchor: 'middle', fill: '#6b7280', fontSize: '11px' }
              } : undefined}
            />
            <Tooltip content={<CustomTooltip />} />
            <Area 
              type="monotone" 
              dataKey="visits" 
              stroke="#2C5AA2" 
              strokeWidth={2}
              fill="url(#colorVisits)"
              dot={false}
              activeDot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      
      {showTitle && (
        <div style={{
          marginTop: '0.25rem',
          padding: '0.375rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '0.7rem',
          color: '#6b7280'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Total Visits: <strong style={{ color: '#374151' }}>{chartData.reduce((sum, month) => sum + month.visits, 0)}</strong></span>
            <span>Period: <strong style={{ color: '#374151' }}>
              {chartData.length > 0 ? 
                `${formatPeriodLabel(chartData[0].month)} - ${formatPeriodLabel(chartData[chartData.length - 1].month)}` : 
                'N/A'
              }
            </strong></span>
          </div>
        </div>
      )}
    </div>
  );
}
